import { getRandomColor } from 'module/helpers/get-random-color';
import { DatePicker } from 'module/ui/components/atoms/date-picker';
import { NoValue } from 'module/ui/components/atoms/no-value/empty-cell';
import { OutsideClickHandler } from 'module/ui/components/atoms/outside-click-handler';
import { Popover } from 'module/ui/components/atoms/popover';
import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { PopoverSelect } from 'module/ui/components/atoms/rich-text-editor/utils/select';
import { Label01 } from 'module/ui/components/styles';
import { Color } from 'module/ui/components/styles';
import { Tag } from '@rocketlane/ui';
import {
  NodeViewProps,
  NodeViewWrapper,
  NodeViewContent,
  Editor as TiptapEditorType,
} from '@tiptap/react';
import moment from 'moment';
import { useState } from 'react';
import styled from 'styled-components';
import type { Option } from '@rocketlane/ui';

const StyledPopoverTitle = styled.div`
  ${Label01};
  align-items: center;
  align-self: stretch;
  color: var(--text-tertiary);
  height: 24px;
  display: flex;
  flex-direction: row;
  gap: var(--spacing-02);
  margin: var(--spacing-03) var(--spacing-04) var(--spacing-02);
`;
const StyledPopover = styled(Popover)`
  position: relative;
  z-index: 9999;
  min-width: 260px;
  width: fit-content;
  max-height: 260px;
  overflow: visible;

  .ozone-popover-content {
    overflow: visible;
    max-height: none;
  }

  .ant-popover-inner-content {
    width: 100%;
  }
`;

const CitationsOnlyWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  /* Hide all content by default */
  & > * {
    display: contents;
  }

  /* Hide all text nodes and paragraph content */
  p {
    font-size: 0 !important;
    line-height: 0 !important;
    color: transparent !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  /* Hide direct text content */
  p::before,
  p::after {
    content: none !important;
  }

  /* Make only citations visible and center them */
  sup,
  [data-citation] {
    font-size: var(--font-size-caption) !important;
    line-height: normal !important;
    color: var(--text-primary) !important;
    display: inline-flex !important;
    align-items: center !important;
    vertical-align: middle !important;
    position: relative !important;
    top: 0 !important;
  }

  /* Remove any spacing from paragraphs */
  p {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const TagsAndCitationsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  width: 100%;
  overflow: hidden;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
  max-width: 100%;
`;

const TruncatedTagText = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTag = styled(Tag)`
  max-width: 100%;
  min-width: 0;
  flex-shrink: 1;

  /* Target the text content inside the tag */
  & > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;
const SingleSelectInput = ({
  node,
  editor,
  getPos,
  choiceOptions,
  title,
}: {
  node: NodeViewProps['node'];
  editor: TiptapEditorType;
  getPos: NodeViewProps['getPos'];
  choiceOptions: { value: string; label: string; color: string }[];
  title?: string;
}) => {
  const handleChange = (selectedValue: Option | readonly Option[] | null) => {
    if (!selectedValue || Array.isArray(selectedValue)) {
      return;
    }

    if (!getPos || !editor) {
      return;
    }

    const option = selectedValue as Option;
    const pos = getPos();

    if (typeof pos === 'number') {
      const tr = editor.state.tr;
      const $pos = editor.state.doc.resolve(pos);
      const contentStart = $pos.pos + 1;
      const contentEnd = $pos.pos + node.nodeSize - 1;

      tr.delete(contentStart, contentEnd);
      tr.insertText(String(option.value), contentStart);
      editor.view.dispatch(tr);
    }
  };

  return (
    <>
      <StyledPopoverTitle>{title || node.attrs.title}</StyledPopoverTitle>
      <PopoverSelect
        variant="tag"
        options={choiceOptions as Option[]}
        value={
          choiceOptions.find((x) => x.value === node.textContent) as Option
        }
        onChange={handleChange}
        placeholder="Search"
        controlShouldRenderValue={false}
        isLoading={false}
        menuPlacement="auto"
        menuPosition="absolute"
        menuPortalTarget={null}
      />
    </>
  );
};

const MultiSelectInput = ({
  node,
  editor,
  getPos,
  choiceOptions,
  title,
}: {
  node: NodeViewProps['node'];
  editor: TiptapEditorType;
  getPos: NodeViewProps['getPos'];
  choiceOptions: { value: string; label: string; color: string }[];
  title?: string;
}) => {
  const handleChange = (selectedValue: Option | readonly Option[] | null) => {
    if (!selectedValue || !Array.isArray(selectedValue) || !getPos || !editor) {
      return;
    }

    const pos = getPos();

    if (typeof pos === 'number') {
      const tr = editor.state.tr;
      const $pos = editor.state.doc.resolve(pos);
      const contentStart = $pos.pos + 1;
      const contentEnd = $pos.pos + node.nodeSize - 1;

      tr.delete(contentStart, contentEnd);
      tr.insertText(
        selectedValue.map((x) => String(x.value)).join(','),
        contentStart,
      );
      editor.view.dispatch(tr);
    }
  };

  return (
    <>
      <StyledPopoverTitle>{title || node.attrs.title}</StyledPopoverTitle>
      <PopoverSelect
        variant="tag"
        isMulti
        options={choiceOptions as Option[]}
        value={
          choiceOptions.filter((x) =>
            node.textContent?.includes(x.value),
          ) as Option[]
        }
        onChange={handleChange}
        isLoading={false}
        placeholder="Search"
        controlShouldRenderValue={false}
        menuPlacement="auto"
        menuPosition="absolute"
        menuPortalTarget={null}
      />
    </>
  );
};

const InputDisplay = ({
  node,
  choiceOptions,
  isEditable,
}: {
  node: NodeViewProps['node'];
  isEditable: boolean;
  choiceOptions: { value: string; label: string; color: string }[];
}) => {
  const dataType = node.attrs.dataType;

  // Check if the cell has any citations
  const hasCitations = () => {
    let found = false;

    node.content.forEach((childNode) => {
      if (childNode.type.name === 'RL_CITATION') {
        found = true;
      }
      // Check nested content like paragraphs
      if (childNode.content) {
        childNode.content.forEach((grandChildNode) => {
          if (grandChildNode.type.name === 'RL_CITATION') {
            found = true;
          }
        });
      }
    });

    return found;
  };

  const showCitations = hasCitations();

  switch (dataType) {
    case TableDataType.SingleSelect:
    case TableDataType.BOOLEAN: {
      if (!node.textContent || node.textContent.trim() === '') {
        return isEditable ? <NoValue /> : <></>;
      }

      // If there are citations, show everything via NodeViewContent to preserve citations
      // The citations will be rendered naturally by TipTap
      if (showCitations) {
        return (
          <TagsAndCitationsContainer>
            <StyledTag
              color={
                ((choiceOptions.find(
                  (choice) => choice.value === node.textContent,
                )?.color as Color) ??
                  (getRandomColor(node.textContent) as Color)) as any
              }
              variant="basic"
            >
              <TruncatedTagText>{node.textContent}</TruncatedTagText>
            </StyledTag>
            <CitationsOnlyWrapper>
              <NodeViewContent />
            </CitationsOnlyWrapper>
          </TagsAndCitationsContainer>
        );
      }

      // No citations - just show the tag
      return (
        <Tag
          color={
            ((choiceOptions.find((choice) => choice.value === node.textContent)
              ?.color as Color) ??
              (getRandomColor(node.textContent) as Color)) as any
          }
          variant="basic"
        >
          {node.textContent}
        </Tag>
      );
    }
    case TableDataType.MultiSelect: {
      const values = node.textContent?.split(',') || [];

      if (
        !node.textContent ||
        node.textContent.trim() === '' ||
        values.length === 0
      ) {
        return isEditable ? <NoValue /> : <></>;
      }

      // If there are citations, show tags + citations
      if (showCitations) {
        return (
          <TagsAndCitationsContainer>
            <TagsContainer>
              {values.map((value) => (
                <StyledTag
                  key={value}
                  color={
                    ((choiceOptions.find((choice) => choice.value === value)
                      ?.color as Color) ??
                      (getRandomColor(value) as Color)) as any
                  }
                  variant="basic"
                >
                  <TruncatedTagText>{value}</TruncatedTagText>
                </StyledTag>
              ))}
            </TagsContainer>
            <CitationsOnlyWrapper>
              <NodeViewContent />
            </CitationsOnlyWrapper>
          </TagsAndCitationsContainer>
        );
      }

      // No citations - just show the tags
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {values.map((value) => (
            <Tag
              key={value}
              color={
                ((choiceOptions.find((choice) => choice.value === value)
                  ?.color as Color) ?? (getRandomColor(value) as Color)) as any
              }
              variant="basic"
            >
              {value}
            </Tag>
          ))}
        </div>
      );
    }
    default: {
      return <NodeViewContent />;
    }
  }
};
const InputRenderer = ({
  node,
  editor,
  getPos,
  choiceOptions,
}: {
  choiceOptions: { value: string; label: string; color: string }[];
  node: NodeViewProps['node'];
  editor: TiptapEditorType;
  getPos: NodeViewProps['getPos'];
}) => {
  switch (node.attrs.dataType) {
    case TableDataType.SingleSelect:
    case TableDataType.BOOLEAN:
      return (
        <SingleSelectInput
          node={node}
          editor={editor}
          getPos={getPos}
          choiceOptions={choiceOptions}
          title={node.attrs.title}
        />
      );
    case TableDataType.MultiSelect:
      return (
        <MultiSelectInput
          node={node}
          editor={editor}
          getPos={getPos}
          choiceOptions={choiceOptions}
          title={node.attrs.title}
        />
      );
    default:
      return <NodeViewContent />;
  }
};

export const CustomTableCell = ({ node, editor, getPos }: NodeViewProps) => {
  const [isEditing, setIsEditing] = useState(false);

  // Use editor.isEditable so cells respect current editor readonly state even when
  // node.attrs.readonly is stale (schema default is fixed at editor creation / content load)
  const isReadonly = node.attrs.readonly || !editor.isEditable;

  const handleClick = () => {
    if (isReadonly) {
      return;
    }
    if (editor.isEditable) {
      setIsEditing(true);

      return;
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Triple-click causes browser to select all content in the cell at the DOM level,
    // creating a selection that doesn't align with ProseMirror's model — which causes
    // ProseMirror to reconcile by clearing the cell content. Prevent default on triple-click.
    if (e.detail >= 3) {
      e.preventDefault();
    }
  };

  // The plugin (syncMultiQuestionTableCells) keeps node.attrs in sync with MULTI_QUESTION config
  // So we can use node.attrs directly - no need to look up the parent section
  const choiceOptions = (node.attrs.choices || []).map((choice: string) => ({
    value: choice,
    label: choice,
    color: getRandomColor(choice),
  }));

  const isEditable = !isReadonly && isEditing;
  const isContentEditable = !isReadonly && !node.attrs.ghost;

  return (
    <NodeViewWrapper
      contentEditable={isContentEditable}
      as="div"
      className={`table-cell-view ${!node.attrs.ghost && isEditing && !node.attrs.isInsertOnlyMode ? 'focused' : ''}`}
      style={{ userSelect: 'text' }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onBlur={handleBlur}
      data-type={node.attrs.dataType}
    >
      {!node.attrs.ghost && (
        <OutsideClickHandler
          onOutsideClick={handleBlur}
          className="table-cell-wrapper"
        >
          {[
            TableDataType.SingleSelect,
            TableDataType.MultiSelect,
            TableDataType.BOOLEAN,
          ].includes(node.attrs.dataType) ? (
            <StyledPopover
              placement="bottomLeft"
              content={
                <InputRenderer
                  node={node}
                  editor={editor}
                  getPos={getPos}
                  choiceOptions={choiceOptions}
                />
              }
              open={isEditing}
            >
              <InputDisplay
                node={node}
                choiceOptions={choiceOptions}
                isEditable={isEditable}
              />
            </StyledPopover>
          ) : (
            <>
              {node.attrs.dataType === TableDataType.DATE ? (
                <DatePicker
                  suffixIcon={null}
                  disabled={isReadonly}
                  value={
                    node.textContent &&
                    node.textContent.trim() !== '' &&
                    node.textContent.trim() !== 'N/A'
                      ? moment(node.textContent)
                      : null
                  }
                  onChange={(date) => {
                    if (getPos && editor) {
                      const pos = getPos();

                      if (typeof pos === 'number') {
                        // Direct transaction approach
                        const tr = editor.state.tr;
                        const $pos = editor.state.doc.resolve(pos);

                        // Get the content positions within the cell (skip the cell node itself)
                        const contentStart = $pos.pos + 1; // +1 to skip the cell node
                        const contentEnd = $pos.pos + node.nodeSize - 1; // -1 to exclude the closing cell node

                        // Delete existing content and insert new content
                        tr.delete(contentStart, contentEnd);
                        tr.insertText(
                          date?.format('YYYY-MM-DD') || '',
                          contentStart,
                        );

                        editor.view.dispatch(tr);
                      }
                    }
                  }}
                  allowClear={false}
                  placeholder="Select date"
                />
              ) : (
                <NodeViewContent />
              )}
            </>
          )}
        </OutsideClickHandler>
      )}
    </NodeViewWrapper>
  );
};
